import {
    Button,
    Loader,
    OnboardingScreenContainer,
    Text,
    Title
} from "dash-styled"
import { Fragment, useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { startOnboarding } from "../../../../../../../Actions/API/AppTemplate/Interface/TemplateInstantiateActions"
import { useMarketplaceTemplate } from "../../../../../../../Hooks/useMarketplaceTemplate"
import { useOrgs } from "../../../../../../../Hooks/useOrgs"
import { TemplateSlug } from "../../../../../../../Models/API/AppTemplateModel"
import {
    hasResult,
    hasValue
} from "../../../../../../../Models/Client/FetchState"
import { TemplateInstantiateStage } from "../../../../../../../Models/Client/TemplateInstantiation"
import { OnboardingProgress } from "../../../../Common/OnboardingProgress/OnboardingProgress"
import { TemplateNotFound } from "../../../../Common/TemplateNotFound"
import { OnboardingOrgChooserPanel } from "./OnboardingOrgChooserPanel"

type OrgChooserProps = {
    templateSlug: TemplateSlug
}

export const OrgChooser = ({ templateSlug }: OrgChooserProps) => {
    const dispatch = useDispatch()

    const orgsFetch = useOrgs()
    const data = useMarketplaceTemplate(undefined, templateSlug)

    const [value, setValue] = useState<string>()

    const showOrgChooser = hasValue(orgsFetch) && orgsFetch.value.length > 0

    const beginOnboarding = useCallback(
        ({
            orgUsername,
            skipOrgChoose
        }: {
            orgUsername?: string
            skipOrgChoose?: boolean
        }) => {
            if (!hasValue(data) || data.value.type !== "implemented") {
                return
            }

            dispatch(
                startOnboarding(
                    orgUsername,
                    data.value.template.name,
                    data.value.template,
                    undefined,
                    skipOrgChoose
                )
            )
        },
        [data, dispatch]
    )

    useEffect(() => {
        if (hasValue(orgsFetch) && !showOrgChooser) {
            beginOnboarding({ skipOrgChoose: true })
        }
    }, [orgsFetch, showOrgChooser, beginOnboarding])

    const handleGetStarted = useCallback(
        () => beginOnboarding?.({ orgUsername: value }),
        [beginOnboarding, value]
    )

    if (hasResult(data) && data.isError && data.message === "Not found") {
        return <TemplateNotFound />
    }

    return hasResult(data) ? (
        <OnboardingScreenContainer forward={false}>
            <OnboardingProgress
                currentStage={TemplateInstantiateStage.orgChoose}
                back={false}
            />
            <div
                id="maincontent"
                className="flex flex-1 flex-col justify-center md:w-3/4 lg:w-2/3 xl:w-1/2"
                data-testid="onboarding-choose-organization"
            >
                {showOrgChooser ? (
                    <>
                        <Title
                            color="darkviolet"
                            variant="heading1"
                            className="text-center"
                            weight="semibold"
                        >
                            Choose organization
                        </Title>
                        <Text
                            component="span"
                            color="gray"
                            className="text-center px-16 mx-4"
                        >
                            Project will be created in selected organization.
                        </Text>

                        <div className="py-6">
                            <OnboardingOrgChooserPanel
                                name="Personal account"
                                isSelected={value === undefined}
                                onChange={() => setValue(undefined)}
                            />
                            {orgsFetch.value.map(org =>
                                org.role === "MEMBER" ? (
                                    <Fragment key={org.id} />
                                ) : (
                                    <OnboardingOrgChooserPanel
                                        key={org.id}
                                        name={org.orgUsername}
                                        isSelected={value === org.orgUsername}
                                        onChange={() =>
                                            setValue(org.orgUsername)
                                        }
                                    />
                                )
                            )}
                        </div>
                        <Button
                            className="w-48 mx-auto"
                            onClick={handleGetStarted}
                        >
                            Get started
                        </Button>
                    </>
                ) : (
                    <Loader />
                )}
            </div>
        </OnboardingScreenContainer>
    ) : null
}
